import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { API } from 'aws-amplify';
import type { AppDispatch, RootState } from '../store';
import { RequestStatus } from '../utils';
import { InstitutionType } from '../../types/user';

/**
 * School directory (PTEC-576) — jurisdiction-keyed reference data behind the
 * education-history school picker. The client narrows country → subdivision →
 * institution type, fetches that ONE scope's list (small, cacheable), and all
 * name filtering happens locally: no keystroke ever leaves the device.
 *
 * A country without a directory reports `supported: false` (the server's Null
 * Object provider) and the UI stays free text.
 */

/** Feature flag key — app admin enables per environment (Admin → Feature Flags). */
export const SCHOOL_DIRECTORY_FLAG = 'school-directory-enabled';

/**
 * DEV-ONLY preview: in development builds the thunks first try a local fixture
 * (public/fixtures/, produced by the import script's --out flag — gitignored,
 * so generating the file IS the opt-in) and fall back to the real API when the
 * file is absent. No env var, no restart: a page reload picks it up. The
 * NODE_ENV guard means production builds eliminate this path entirely.
 * The real gate stays the `school-directory-enabled` feature flag, managed
 * like every other flag (catalogue + Admin → Feature Flags).
 */
export const SCHOOL_DIRECTORY_DEV_PREVIEW = process.env.NODE_ENV === 'development';

interface DirectoryFixture {
	country: string;
	subdivisions: { code: string; name: string }[];
	scopes: Record<string, DirectorySchool[]>;
}

let fixturePromise: Promise<DirectoryFixture | null> | null = null;
const loadFixture = (): Promise<DirectoryFixture | null> => {
	fixturePromise =
		fixturePromise ??
		fetch('/fixtures/schoolDirectory-US.json')
			.then((response) => (response.ok ? response.json() : null))
			.catch(() => null);
	return fixturePromise;
};

export interface DirectorySchool {
	name: string;
	city?: string;
	/** US state code. Present from PTEC-643 — the cross-state disambiguator. */
	state?: string;
	/** `nces:{id}` / `ipeds:{id}`; stored on the entry so the pick is canonical. */
	sourceId?: string;
	/** Canonical stage, used to prefill the type pill on selection. */
	type?: InstitutionType;
}

interface CountryDirectory {
	supported: boolean;
	subdivisions: { code: string; name: string }[];
	/** Dataset provenance (admin tiles/cards): source vintage + import info. */
	year?: number;
	counts?: Record<string, number>;
	importedAt?: string;
	source?: string;
	status: RequestStatus;
}

interface ScopeEntry {
	schools: DirectorySchool[];
	status: RequestStatus;
}

interface SchoolDirectoryState {
	byCountry: Record<string, CountryDirectory>;
	scopes: Record<string, ScopeEntry>;
	/**
	 * Cross-state name search (PTEC-643), keyed by `{country}#{normalised query}`.
	 * Keyed rather than a single slot deliberately: keystrokes race, and a slower
	 * response for "linc" must not overwrite the results for "lincoln".
	 */
	searches: Record<string, ScopeEntry>;
}

const initialState: SchoolDirectoryState = { byCountry: {}, scopes: {}, searches: {} };

export const scopeKey = (country: string, subdivision: string, institutionType: string) =>
	`${country}#${subdivision}#${institutionType}`;

export const fetchCountryDirectory = createAsyncThunk<
	{
		country: string;
		supported: boolean;
		subdivisions: { code: string; name: string }[];
		year?: number;
		counts?: Record<string, number>;
		importedAt?: string;
		source?: string;
	},
	string,
	{ dispatch: AppDispatch; state: RootState }
>('schoolDirectory/fetchCountry', async (country) => {
	if (SCHOOL_DIRECTORY_DEV_PREVIEW) {
		const fixture = await loadFixture();
		if (fixture) {
			const supported = fixture.country === country;
			return {
				country,
				supported,
				subdivisions: supported ? fixture.subdivisions : [],
				year: supported ? (fixture as { year?: number }).year : undefined,
				source: supported ? (fixture as { source?: string }).source : undefined
			};
		}
	}
	const response = await API.get('inspirusapi', `/school-directory/${encodeURIComponent(country)}`, {});
	return {
		country,
		supported: response?.supported === true,
		subdivisions: Array.isArray(response?.subdivisions) ? response.subdivisions : [],
		year: response?.year,
		counts: response?.counts,
		importedAt: response?.importedAt,
		source: response?.source
	};
});

export const fetchSchools = createAsyncThunk<
	{ key: string; schools: DirectorySchool[] },
	{ country: string; subdivision: string; institutionType: string },
	{ dispatch: AppDispatch; state: RootState }
>('schoolDirectory/fetchSchools', async ({ country, subdivision, institutionType }) => {
	if (SCHOOL_DIRECTORY_DEV_PREVIEW) {
		const fixture = await loadFixture();
		if (fixture) {
			return {
				key: scopeKey(country, subdivision, institutionType),
				schools: (fixture.country === country && fixture.scopes[`${subdivision}#${institutionType}`]) || []
			};
		}
	}
	const response = await API.get(
		'inspirusapi',
		`/school-directory/${encodeURIComponent(country)}/schools?subdivision=${encodeURIComponent(
			subdivision
		)}&type=${encodeURIComponent(institutionType)}`,
		{}
	);
	return {
		key: scopeKey(country, subdivision, institutionType),
		schools: Array.isArray(response?.schools) ? response.schools : []
	};
});

/** Minimum the server accepts (`q must be between 3 and 64 characters`). */
export const SEARCH_MIN_CHARS = 3;

export const searchKey = (country: string, query: string) => `${country}#${query.trim().toLowerCase()}`;

export const searchSchools = createAsyncThunk<
	{ key: string; schools: DirectorySchool[] },
	{ country: string; query: string },
	{ dispatch: AppDispatch; state: RootState }
>('schoolDirectory/searchSchools', async ({ country, query }) => {
	const key = searchKey(country, query);
	if (SCHOOL_DIRECTORY_DEV_PREVIEW) {
		const fixture = await loadFixture();
		if (fixture) {
			const needle = query.trim().toLowerCase();
			const all = Object.values(fixture.scopes ?? {}).flat() as DirectorySchool[];
			return { key, schools: all.filter((school) => school.name.toLowerCase().includes(needle)).slice(0, 50) };
		}
	}
	const response = await API.get(
		'inspirusapi',
		`/school-directory/${encodeURIComponent(country)}/search?q=${encodeURIComponent(query.trim())}`,
		{}
	);
	return { key, schools: Array.isArray(response?.schools) ? response.schools : [] };
});

const schoolDirectorySlice = createSlice({
	name: 'schoolDirectory',
	initialState,
	reducers: {
		resetSchoolDirectory: () => initialState
	},
	extraReducers: (builder) => {
		builder.addCase(fetchCountryDirectory.pending, (state, action) => {
			state.byCountry[action.meta.arg] = {
				supported: false,
				subdivisions: [],
				status: RequestStatus.PENDING
			};
		});
		builder.addCase(fetchCountryDirectory.fulfilled, (state, action) => {
			state.byCountry[action.payload.country] = {
				supported: action.payload.supported,
				subdivisions: action.payload.subdivisions,
				year: action.payload.year,
				counts: action.payload.counts,
				importedAt: action.payload.importedAt,
				source: action.payload.source,
				status: RequestStatus.SUCCESS
			};
		});
		builder.addCase(fetchCountryDirectory.rejected, (state, action) => {
			// Fail safe to free text — an outage must never block profile editing.
			state.byCountry[action.meta.arg] = { supported: false, subdivisions: [], status: RequestStatus.ERROR };
		});
		builder.addCase(fetchSchools.pending, (state, action) => {
			const key = scopeKey(action.meta.arg.country, action.meta.arg.subdivision, action.meta.arg.institutionType);
			state.scopes[key] = { schools: [], status: RequestStatus.PENDING };
		});
		builder.addCase(fetchSchools.fulfilled, (state, action) => {
			state.scopes[action.payload.key] = { schools: action.payload.schools, status: RequestStatus.SUCCESS };
		});
		builder.addCase(fetchSchools.rejected, (state, action) => {
			const key = scopeKey(action.meta.arg.country, action.meta.arg.subdivision, action.meta.arg.institutionType);
			state.scopes[key] = { schools: [], status: RequestStatus.ERROR };
		});
		builder.addCase(searchSchools.pending, (state, action) => {
			state.searches[searchKey(action.meta.arg.country, action.meta.arg.query)] = {
				schools: [],
				status: RequestStatus.PENDING
			};
		});
		builder.addCase(searchSchools.fulfilled, (state, action) => {
			state.searches[action.payload.key] = { schools: action.payload.schools, status: RequestStatus.SUCCESS };
		});
		builder.addCase(searchSchools.rejected, (state, action) => {
			// Same fail-safe as the country fetch: no suggestions, but the field stays
			// usable as free text. A directory outage must never block profile editing.
			state.searches[searchKey(action.meta.arg.country, action.meta.arg.query)] = {
				schools: [],
				status: RequestStatus.ERROR
			};
		});
	}
});

export const { resetSchoolDirectory } = schoolDirectorySlice.actions;
export default schoolDirectorySlice.reducer;
